import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Input } from './ui/input';
import { toast } from 'sonner';
import client from '../api/client';
import { GripVertical, Plus, X, ChevronUp, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { computeRotatingPreview, WEEKDAY_KEYS } from '../lib/rotatingSchedule';

/**
 * WeekPlannerModal — assign routine days to days of the week, or set up a training cycle.
 *
 * Props:
 *   open: bool
 *   onClose: () => void
 *   routine: { id, name, days: [{ id, day_name }], week_schedule?, schedule_mode?, cycle_schedule?, cycle_rest_days? }
 *   isPersonal: bool
 *   onSaved: (scheduleData) => void
 */
export default function WeekPlannerModal({ open, onClose, routine, isPersonal = false, onSaved }) {
  const { t } = useTranslation();
  const WEEKDAYS = WEEKDAY_KEYS.map(key => ({ key, label: t(`weekPlanner.${key}`) }));
  const [activeTab, setActiveTab] = useState('weekly');
  const [schedule, setSchedule] = useState({});
  const [cycleSchedule, setCycleSchedule] = useState([]);
  const [cycleRestDays, setCycleRestDays] = useState(1);
  const [rotation, setRotation] = useState([]);          // array of day_ids
  const [restWeekdays, setRestWeekdays] = useState([]);  // array of weekday keys
  const [saving, setSaving] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);

  // Initialise from existing data
  useEffect(() => {
    if (!routine) return;
    setActiveTab(routine.schedule_mode === 'cycle' ? 'cycle' : routine.schedule_mode === 'rotating' ? 'rotating' : 'weekly');
    const initial = {};
    WEEKDAYS.forEach(({ key }) => {
      const v = routine.week_schedule?.[key];
      initial[key] = Array.isArray(v) ? v.filter(x => x && x !== 'rest') : (v && v !== 'rest' ? [v] : []);
    });
    setSchedule(initial);
    setCycleSchedule(routine.schedule_mode === 'cycle' ? (routine.cycle_schedule || []) : []);
    setCycleRestDays(routine.cycle_rest_days ?? 1);
    setRotation(routine.schedule_mode === 'rotating' ? (routine.cycle_schedule || []) : []);
    setRestWeekdays(routine.rest_weekdays || []);
  }, [routine?.id, open]);

  // schedule[day] is an array of 0–2 day_ids. slot 0 = primary, slot 1 = second workout.
  const setSlot = (day, slot, value) => {
    setSchedule(prev => {
      const arr = [...(prev[day] || [])];
      if (value === 'rest') {
        if (slot === 0) return { ...prev, [day]: [] };
        return { ...prev, [day]: arr.slice(0, 1) };
      }
      arr[slot] = value;
      const deduped = arr.filter((v, i) => v && arr.indexOf(v) === i);
      return { ...prev, [day]: deduped };
    });
  };
  const addSecondSlot = (day) => {
    setSchedule(prev => {
      const arr = prev[day] || [];
      if (arr.length !== 1) return prev;
      const candidate = (routine.days || []).find(d => d.id !== arr[0]);
      return candidate ? { ...prev, [day]: [...arr, candidate.id] } : prev;
    });
  };
  const removeSecondSlot = (day) => {
    setSchedule(prev => ({ ...prev, [day]: (prev[day] || []).slice(0, 1) }));
  };

  // Cycle helpers
  const addDayToCycle = (dayId) => {
    setCycleSchedule(prev => [...prev, dayId]);
  };
  const removeDayFromCycle = (index) => {
    setCycleSchedule(prev => prev.filter((_, i) => i !== index));
  };

  // Rotating helpers
  const toggleRestWeekday = (key) => {
    setRestWeekdays(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };
  const addToRotation = (dayId) => setRotation(prev => [...prev, dayId]);
  const removeFromRotation = (idx) => setRotation(prev => prev.filter((_, i) => i !== idx));
  const moveInRotation = (idx, dir) => setRotation(prev => {
    const next = [...prev]; const j = idx + dir;
    if (j < 0 || j >= next.length) return prev;
    [next[idx], next[j]] = [next[j], next[idx]];
    return next;
  });

  // Drag reorder (desktop) + tap-to-reorder (touch)
  const handleDragStart = (idx) => setDragIdx(idx);
  const handleDragOver = (e, idx) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx) return;
    setCycleSchedule(prev => {
      const next = [...prev];
      const [moved] = next.splice(dragIdx, 1);
      next.splice(idx, 0, moved);
      return next;
    });
    setDragIdx(idx);
  };
  const handleDragEnd = () => setDragIdx(null);
  const moveDay = (idx, delta) => {
    setCycleSchedule(prev => {
      const target = idx + delta;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  };

  const handleSave = async () => {
    if (!routine?.id) return;
    setSaving(true);
    try {
      if (activeTab === 'weekly') {
        const endpoint = isPersonal
          ? `/routines/personal/${routine.id}/week-schedule`
          : `/routines/${routine.id}/week-schedule`;
        const res = await client.patch(endpoint, { week_schedule: schedule });
        toast.success(t('weekPlanner.savedSuccess'));
        // Use the server-collapsed schedule (string | list | "rest") so parent state
        // mirrors the DB canonical shape rather than our local array form.
        onSaved?.({ week_schedule: res.data?.week_schedule ?? schedule, schedule_mode: 'weekly' });
      } else if (activeTab === 'cycle') {
        if (cycleSchedule.length === 0) {
          toast.error(t('weekPlanner.saveError'));
          setSaving(false);
          return;
        }
        const endpoint = isPersonal
          ? `/routines/personal/${routine.id}/cycle-schedule`
          : `/routines/${routine.id}/cycle-schedule`;
        await client.patch(endpoint, { cycle_schedule: cycleSchedule, cycle_rest_days: cycleRestDays });
        toast.success(t('weekPlanner.savedSuccess'));
        onSaved?.({ cycle_schedule: cycleSchedule, cycle_rest_days: cycleRestDays, schedule_mode: 'cycle' });
      } else if (activeTab === 'rotating') {
        if (rotation.length === 0) { toast.error(t('weekPlanner.saveError')); setSaving(false); return; }
        const endpoint = isPersonal
          ? `/routines/personal/${routine.id}/rotating-schedule`
          : `/routines/${routine.id}/rotating-schedule`;
        const res = await client.patch(endpoint, { rotation, rest_weekdays: restWeekdays });
        toast.success(t('weekPlanner.savedSuccess'));
        onSaved?.({ schedule_mode: 'rotating', cycle_schedule: res.data?.cycle_schedule ?? rotation, rest_weekdays: res.data?.rest_weekdays ?? restWeekdays });
        onClose();
        return;
      }
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('weekPlanner.saveError'));
    } finally {
      setSaving(false);
    }
  };

  if (!routine) return null;

  const days = routine.days || [];
  const dayMap = {};
  days.forEach(d => { dayMap[d.id] = d.day_name; });

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !saving) onClose(); }}>
      <DialogContent className="max-w-md w-full max-h-[90dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Barlow Condensed, sans-serif' }} className="text-xl uppercase">
            {t('weekPlanner.title')}
          </DialogTitle>
          <p className="text-sm text-muted-foreground">{routine.name}</p>
        </DialogHeader>

        {/* Tab bar */}
        <div className="flex border-b border-border mb-3">
          <button
            onClick={() => setActiveTab('weekly')}
            className={`flex-1 pb-2 text-sm font-medium transition-colors border-b-2 ${
              activeTab === 'weekly'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {t('weekPlanner.weeklyTab')}
          </button>
          <button
            onClick={() => setActiveTab('cycle')}
            className={`flex-1 pb-2 text-sm font-medium transition-colors border-b-2 ${
              activeTab === 'cycle'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {t('weekPlanner.trainingCycleTab')}
          </button>
          <button
            onClick={() => setActiveTab('rotating')}
            className={`flex-1 pb-2 text-sm font-medium transition-colors border-b-2 ${
              activeTab === 'rotating' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {t('weekPlanner.rotatingTab')}
          </button>
        </div>

        {/* Weekly tab */}
        {activeTab === 'weekly' && (
          <div className="space-y-3 py-2">
            {days.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                {t('weekPlanner.noWorkoutDays')}
              </p>
            ) : (
              WEEKDAYS.map(({ key, label }) => {
                const slots = schedule[key] || [];
                const primary = slots[0] || 'rest';
                const second = slots[1];
                return (
                  <div key={key} className="flex items-start justify-between gap-3">
                    <span className="text-sm font-medium w-24 shrink-0 pt-2.5">{label}</span>
                    <div className="flex-1 space-y-1.5">
                      {/* Primary workout */}
                      <Select value={primary} onValueChange={(v) => setSlot(key, 0, v)}>
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="rest">
                            <span className="text-muted-foreground">{t('weekPlanner.restDay')}</span>
                          </SelectItem>
                          {days.map(day => (
                            <SelectItem key={day.id} value={day.id}>{day.day_name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      {/* Second workout slot */}
                      {primary !== 'rest' && second != null && (
                        <div className="flex items-center gap-2">
                          <Select value={second} onValueChange={(v) => setSlot(key, 1, v)}>
                            <SelectTrigger className="flex-1">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {days.filter(day => day.id !== primary).map(day => (
                                <SelectItem key={day.id} value={day.id}>{day.day_name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <button
                            type="button"
                            onClick={() => removeSecondSlot(key)}
                            className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded text-muted-foreground hover:text-destructive active:bg-destructive/10 transition-colors shrink-0"
                            aria-label={t('weekPlanner.removeSecondWorkout')}
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      )}

                      {/* Add-second link */}
                      {primary !== 'rest' && second == null && days.length > 1 && (
                        <button
                          type="button"
                          onClick={() => addSecondSlot(key)}
                          className="flex items-center gap-1 text-xs text-primary hover:underline min-h-[32px]"
                        >
                          <Plus className="w-3.5 h-3.5" /> {t('weekPlanner.addSecondWorkout')}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* Training Cycle tab */}
        {activeTab === 'cycle' && (
          <div className="space-y-4 py-2">
            {days.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                {t('weekPlanner.noWorkoutDays')}
              </p>
            ) : (
              <>
                {/* Ordered cycle list */}
                <div>
                  <label className="text-sm font-medium mb-2 block">Workout Order</label>
                  {cycleSchedule.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-3 border border-dashed border-border rounded-md">
                      No days added yet. Add workout days below.
                    </p>
                  ) : (
                    <div className="space-y-1">
                      {cycleSchedule.map((dayId, idx) => (
                        <div
                          key={`${dayId}-${idx}`}
                          draggable
                          onDragStart={() => handleDragStart(idx)}
                          onDragOver={(e) => handleDragOver(e, idx)}
                          onDragEnd={handleDragEnd}
                          className={`flex items-center gap-2 p-2 rounded-md border transition-colors ${
                            dragIdx === idx ? 'border-primary bg-primary/10' : 'border-border bg-card'
                          }`}
                        >
                          <GripVertical className="hidden md:block w-4 h-4 text-muted-foreground cursor-grab shrink-0" aria-hidden />
                          <span className="text-xs font-bold text-muted-foreground w-6">{idx + 1}.</span>
                          <span className="text-sm flex-1 truncate">{dayMap[dayId] || 'Unknown Day'}</span>
                          {/* Touch-friendly up/down (works on all platforms; replaces HTML5 DnD on touch) */}
                          <button
                            type="button"
                            onClick={() => moveDay(idx, -1)}
                            disabled={idx === 0}
                            className="min-h-[44px] min-w-[44px] md:min-h-[28px] md:min-w-[28px] flex items-center justify-center rounded text-muted-foreground hover:text-foreground active:bg-muted disabled:opacity-30 disabled:pointer-events-none transition-colors"
                            aria-label={t('common.moveUp', 'Move up')}
                          >
                            <ChevronUp className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => moveDay(idx, 1)}
                            disabled={idx === cycleSchedule.length - 1}
                            className="min-h-[44px] min-w-[44px] md:min-h-[28px] md:min-w-[28px] flex items-center justify-center rounded text-muted-foreground hover:text-foreground active:bg-muted disabled:opacity-30 disabled:pointer-events-none transition-colors"
                            aria-label={t('common.moveDown', 'Move down')}
                          >
                            <ChevronDown className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => removeDayFromCycle(idx)}
                            className="min-h-[44px] min-w-[44px] md:min-h-[28px] md:min-w-[28px] flex items-center justify-center rounded text-muted-foreground hover:text-destructive active:bg-destructive/10 transition-colors"
                            aria-label={t('common.remove', 'Remove')}
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Add day dropdown */}
                  <div className="mt-2">
                    <Select onValueChange={(v) => addDayToCycle(v)}>
                      <SelectTrigger className="w-full">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Plus className="w-4 h-4" /> Add workout day
                        </div>
                      </SelectTrigger>
                      <SelectContent>
                        {days.map(day => (
                          <SelectItem key={day.id} value={day.id}>
                            {day.day_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Rest days per cycle */}
                <div>
                  <label className="text-sm font-medium mb-1 block">Minimum rest days per cycle</label>
                  <p className="text-xs text-muted-foreground mb-2">
                    Rest days the user must take before the cycle repeats. Days off between workouts count toward this.
                  </p>
                  <Input
                    type="number"
                    min={0}
                    max={30}
                    value={cycleRestDays}
                    onChange={(e) => setCycleRestDays(Math.max(0, parseInt(e.target.value) || 0))}
                    className="w-24"
                  />
                </div>
              </>
            )}
          </div>
        )}

        {/* Rotating tab */}
        {activeTab === 'rotating' && (
          <div className="space-y-4 py-2">
            {days.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">{t('weekPlanner.noWorkoutDays')}</p>
            ) : (
              <>
                {/* Rest weekday chips */}
                <div>
                  <label className="text-sm font-medium mb-2 block">{t('weekPlanner.restWeekdaysLabel')}</label>
                  <div className="flex flex-wrap gap-2">
                    {WEEKDAY_KEYS.map(key => {
                      const on = restWeekdays.includes(key);
                      return (
                        <button key={key} type="button" onClick={() => toggleRestWeekday(key)}
                          className={`min-h-[44px] px-3 rounded-full border text-sm capitalize transition-colors ${
                            on ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground'
                          }`}>
                          {t(`weekPlanner.${key}`)}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Rotation list */}
                <div>
                  <label className="text-sm font-medium mb-1 block">{t('weekPlanner.workoutRotationLabel')}</label>
                  <p className="text-xs text-muted-foreground mb-2">{t('weekPlanner.rotationHint')}</p>
                  {rotation.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-3 border border-dashed border-border rounded-md">{t('weekPlanner.noWorkoutDays')}</p>
                  ) : (
                    <div className="space-y-1">
                      {rotation.map((dayId, idx) => (
                        <div key={`${dayId}-${idx}`} className="flex items-center gap-2 p-2 rounded-md border border-border bg-card">
                          <span className="text-xs font-bold text-muted-foreground w-6">{idx + 1}.</span>
                          <span className="text-sm flex-1 truncate">{dayMap[dayId] || 'Unknown Day'}</span>
                          <button type="button" onClick={() => moveInRotation(idx, -1)} disabled={idx === 0}
                            className="min-h-[44px] min-w-[44px] md:min-h-[28px] md:min-w-[28px] flex items-center justify-center rounded text-muted-foreground hover:text-foreground active:bg-muted disabled:opacity-30 disabled:pointer-events-none" aria-label={t('common.moveUp', 'Move up')}>
                            <ChevronUp className="w-4 h-4" />
                          </button>
                          <button type="button" onClick={() => moveInRotation(idx, 1)} disabled={idx === rotation.length - 1}
                            className="min-h-[44px] min-w-[44px] md:min-h-[28px] md:min-w-[28px] flex items-center justify-center rounded text-muted-foreground hover:text-foreground active:bg-muted disabled:opacity-30 disabled:pointer-events-none" aria-label={t('common.moveDown', 'Move down')}>
                            <ChevronDown className="w-4 h-4" />
                          </button>
                          <button type="button" onClick={() => removeFromRotation(idx)}
                            className="min-h-[44px] min-w-[44px] md:min-h-[28px] md:min-w-[28px] flex items-center justify-center rounded text-muted-foreground hover:text-destructive active:bg-destructive/10" aria-label={t('common.remove', 'Remove')}>
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="mt-2">
                    <Select onValueChange={(v) => addToRotation(v)}>
                      <SelectTrigger className="w-full">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground"><Plus className="w-4 h-4" /> {t('weekPlanner.addSecondWorkout')}</div>
                      </SelectTrigger>
                      <SelectContent>
                        {days.map(day => (<SelectItem key={day.id} value={day.id}>{day.day_name}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Preview grid */}
                <div>
                  <label className="text-sm font-medium mb-2 block">{t('weekPlanner.previewLabel')}</label>
                  <div className="space-y-2">
                    {computeRotatingPreview(restWeekdays, rotation.map(id => ({ id, name: dayMap[id] || '?' })), 2).map((week, wIdx) => (
                      <div key={wIdx} className="grid grid-cols-7 gap-1">
                        {week.map((cell, i) => (
                          <div key={i} className={`text-[10px] rounded p-1 text-center min-h-[40px] flex flex-col justify-center ${cell.label ? 'bg-primary/10 text-primary' : 'bg-muted/40 text-muted-foreground'}`}>
                            <span className="font-bold uppercase">{t(`weekPlanner.${cell.weekday}`).slice(0, 3)}</span>
                            <span className="truncate">{cell.label || t('weekPlanner.restCell')}</span>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>{t('weekPlanner.cancel')}</Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-primary hover:bg-primary/90"
          >
            {saving ? t('weekPlanner.saving') : t('weekPlanner.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
